import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import type { ConversationFolderItem } from '@/api/hooks/aiAdvisoryQueryActions';

import { InlineRenameInput } from './InlineRenameInput';

interface IFolderPickerProps {
  /** Folders the user can move the conversation into. */
  folders: ConversationFolderItem[];
  /** Folder the conversation is currently in (used to hide that option). null = unfoldered. */
  currentFolderId: string | null;
  /** Label for the "remove from folder" option. */
  noFolderLabel: string;
  /** Label for the "+ Create new folder" option. */
  createNewLabel: string;
  /** Maximum length to enforce when creating a folder inline. */
  maxFolderNameLength: number;
  /** Save / cancel labels for the inline create input. */
  createSaveLabel: string;
  createCancelLabel: string;
  createAriaLabel: string;
  /**
   * When provided the picker renders in a fixed portal anchored below this element,
   * escaping any overflow:hidden ancestor (e.g. the sidebar's collapse animation wrapper).
   */
  anchorEl?: HTMLElement | null;
  onPickExisting: (folderId: string | null) => Promise<void>;
  /** Returns the new folder id once it's been created server-side. */
  onCreateAndPick: (name: string) => Promise<void>;
  onClose: () => void;
}

/**
 * EWM-521 §5.4 — Per-conversation folder picker dropdown. Shown via the folder icon
 * that appears next to a conversation on hover. Closes on outside click, Escape, or after
 * a successful pick.
 */
export const FolderPicker: React.FC<IFolderPickerProps> = ({
  folders,
  currentFolderId,
  noFolderLabel,
  createNewLabel,
  maxFolderNameLength,
  createSaveLabel,
  createCancelLabel,
  createAriaLabel,
  anchorEl,
  onPickExisting,
  onCreateAndPick,
  onClose,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [fixedPos, setFixedPos] = useState<{ top: number; right: number } | null>(null);

  useLayoutEffect(() => {
    if (!anchorEl) return;
    const rect = anchorEl.getBoundingClientRect();
    setFixedPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
  }, [anchorEl]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  const content = (
    <div
      ref={containerRef}
      className='dark:bg-c4 max-h-72 w-56 overflow-y-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg dark:border-(--border)'
      style={
        anchorEl && fixedPos
          ? { position: 'fixed', top: fixedPos.top, right: fixedPos.right, zIndex: 9999 }
          : { position: 'absolute', right: 0, top: '100%', marginTop: 4, zIndex: 20 }
      }
    >
      {/* No folder option — always present so a conversation can be returned to top level */}
      <button
        type='button'
        disabled={currentFolderId === null}
        onClick={async (e) => {
          e.preventDefault();
          e.stopPropagation();
          await onPickExisting(null);
        }}
        className='flex w-full items-center px-3 py-1.5 text-left text-sm hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-(--color-dark-grey-4)'
      >
        {noFolderLabel}
      </button>

      {folders.length > 0 && (
        <div className='my-1 border-t border-gray-200 dark:border-(--border)' />
      )}

      {folders.map((folder) => (
        <button
          key={folder.folderId}
          type='button'
          disabled={folder.folderId === currentFolderId}
          onClick={async (e) => {
            e.preventDefault();
            e.stopPropagation();
            await onPickExisting(folder.folderId);
          }}
          className='flex w-full items-center justify-between px-3 py-1.5 text-left text-sm hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-(--color-dark-grey-4)'
          title={folder.name}
        >
          <span className='truncate'>{folder.name}</span>
          <span className='ml-2 text-xs text-gray-400'>{folder.conversationCount}</span>
        </button>
      ))}

      <div className='my-1 border-t border-gray-200 dark:border-(--border)' />

      {isCreating ? (
        <div className='px-2 py-1'>
          <InlineRenameInput
            initialValue=''
            maxLength={maxFolderNameLength}
            saveLabel={createSaveLabel}
            cancelLabel={createCancelLabel}
            ariaLabel={createAriaLabel}
            autoSelect={false}
            onSave={async (name) => {
              await onCreateAndPick(name);
            }}
            onCancel={() => setIsCreating(false)}
          />
        </div>
      ) : (
        <button
          type='button'
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsCreating(true);
          }}
          className='flex w-full items-center px-3 py-1.5 text-left text-sm font-medium text-(--primary) hover:bg-gray-100 dark:hover:bg-(--color-dark-grey-4)'
        >
          {createNewLabel}
        </button>
      )}
    </div>
  );

  if (anchorEl) {
    return createPortal(content, document.body);
  }
  return content;
};
